/**
 * Normalize a user-typed or API date string to YYYY-MM-DD for `<input type="date">` and APIs.
 * Supports ISO, DD/MM/YYYY, DD-MM-YYYY, and YYYYMMDD (8 digits).
 */
export function parseDateInputToIso(value: string): string {
  const s = (value || '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dmY = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/.exec(s);
  if (dmY) {
    const dd = dmY[1].padStart(2, '0');
    const mm = dmY[2].padStart(2, '0');
    const yyyy = dmY[3];
    return `${yyyy}-${mm}-${dd}`;
  }
  const d8 = s.replace(/\D/g, '').slice(0, 8);
  if (d8.length === 8) {
    const yFirst = parseInt(d8.slice(0, 4), 10);
    if (yFirst >= 1990 && yFirst <= 2100) {
      return `${d8.slice(0, 4)}-${d8.slice(4, 6)}-${d8.slice(6, 8)}`;
    }
    const dd = d8.slice(0, 2);
    const mm = d8.slice(2, 4);
    const yyyy = d8.slice(4, 8);
    return `${yyyy}-${mm}-${dd}`;
  }
  return formatDate(s);
}

export function formatDate(value: any): string {
  if (!value) return '';
  
  // If already in YYYY-MM-DD format, return as is
  if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
    return value.slice(0, 10);
  }
  
  // Handle MM/DD/YY format (like "08/21/25")
  if (typeof value === 'string' && value.match(/^\d{1,2}\/\d{1,2}\/\d{2}$/)) {
    const [month, day, year] = value.split('/');
    const fullYear = parseInt(year) < 50 ? `20${year}` : `19${year}`;
    return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  // Try to parse the date
  try {
    // Ensure value is valid before creating Date object
    if (value === null || value === undefined || value === '') {
      return '';
    }
    
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch (error) {
    // If parsing fails, try to extract date from string
    try {
      const dateMatch = String(value).match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
      if (dateMatch) {
        const year = dateMatch[1];
        const month = String(parseInt(dateMatch[2])).padStart(2, '0');
        const day = String(parseInt(dateMatch[3])).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (innerError) {
      // If even string matching fails, return empty string
      return '';
    }
  }
  
  // If all else fails, return the original value as string
  return String(value);
}

/**
 * Get forward-looking date based on description (for options expiry dates)
 */
export function getForwardDate(description: string): string {
  const today = new Date();
  
  switch (description.toLowerCase()) {
    case 'today':
      return formatDate(today);
    case 'tomorrow':
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      return formatDate(tomorrow);
    case 'this week':
      // Next Friday (end of week)
      const thisWeek = new Date(today);
      const daysUntilFriday = (5 - thisWeek.getDay() + 7) % 7;
      thisWeek.setDate(today.getDate() + daysUntilFriday);
      return formatDate(thisWeek);
    case 'next week':
      // Friday of next week
      const nextWeek = new Date(today);
      const daysUntilNextFriday = (5 - nextWeek.getDay() + 7) % 7 + 7;
      nextWeek.setDate(today.getDate() + daysUntilNextFriday);
      return formatDate(nextWeek);
    case 'this month':
      // Last Friday of current month
      const thisMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const daysUntilLastFriday = (thisMonth.getDay() - 5 + 7) % 7;
      thisMonth.setDate(thisMonth.getDate() - daysUntilLastFriday);
      return formatDate(thisMonth);
    case 'next month':
      // Last Friday of next month
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      const daysUntilNextMonthFriday = (nextMonth.getDay() - 5 + 7) % 7;
      nextMonth.setDate(nextMonth.getDate() - daysUntilNextMonthFriday);
      return formatDate(nextMonth);
    case 'quarterly':
      // Third Friday of current quarter
      const quarter = Math.floor(today.getMonth() / 3);
      const quarterStart = new Date(today.getFullYear(), quarter * 3, 1);
      const thirdFriday = new Date(quarterStart);
      thirdFriday.setDate(quarterStart.getDate() + (5 - quarterStart.getDay() + 7) % 7 + 14);
      return formatDate(thirdFriday);
    case 'next quarter':
      // Third Friday of next quarter
      const nextQuarter = Math.floor(today.getMonth() / 3) + 1;
      const nextQuarterStart = new Date(today.getFullYear(), nextQuarter * 3, 1);
      const nextQuarterFriday = new Date(nextQuarterStart);
      nextQuarterFriday.setDate(nextQuarterStart.getDate() + (5 - nextQuarterStart.getDay() + 7) % 7 + 14);
      return formatDate(nextQuarterFriday);
    case 'yearly':
      // Third Friday of December
      const yearly = new Date(today.getFullYear(), 11, 1);
      yearly.setDate(yearly.getDate() + (5 - yearly.getDay() + 7) % 7 + 14);
      return formatDate(yearly);
    default:
      return formatDate(today);
  }
}

/**
 * Get relative date based on description (for backward compatibility)
 */
export function getRelativeDate(description: string): string {
  const today = new Date();
  
  switch (description.toLowerCase()) {
    case 'today':
      return formatDate(today);
    case 'yesterday':
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return formatDate(yesterday);
    case 'last week':
      const lastWeek = new Date(today);
      lastWeek.setDate(today.getDate() - 7);
      return formatDate(lastWeek);
    case 'last month':
      const lastMonth = new Date(today);
      lastMonth.setMonth(today.getMonth() - 1);
      return formatDate(lastMonth);
    case 'last 3 months':
      const last3Months = new Date(today);
      last3Months.setMonth(today.getMonth() - 3);
      return formatDate(last3Months);
    case 'last 6 months':
      const last6Months = new Date(today);
      last6Months.setMonth(today.getMonth() - 6);
      return formatDate(last6Months);
    case 'last year':
      const lastYear = new Date(today);
      lastYear.setFullYear(today.getFullYear() - 1);
      return formatDate(lastYear);
    default:
      return formatDate(today);
  }
}

/**
 * Check if a date is within a range
 */
export function isDateInRange(date: any, startDate: string, endDate: string): boolean {
  const formattedDate = formatDate(date);
  return formattedDate >= startDate && formattedDate <= endDate;
}

/**
 * Get forward-looking date range options for options expiry filtering
 */
export function getForwardDateRangeOptions(): Array<{ value: string; label: string }> {
  return [
    { value: 'today', label: 'Today (0DT)' },
    { value: 'tomorrow', label: 'Tomorrow (1DT)' },
    { value: 'this week', label: 'This Week (Weekly)' },
    { value: 'next week', label: 'Next Week' },
    { value: 'this month', label: 'This Month (Monthly)' },
    { value: 'next month', label: 'Next Month' },
    { value: 'quarterly', label: 'This Quarter' },
    { value: 'next quarter', label: 'Next Quarter' },
    { value: 'yearly', label: 'This Year (Yearly)' },
    { value: 'custom', label: 'Custom Range' }
  ];
}

/**
 * Get date range options for filtering (backward compatibility)
 */
export function getDateRangeOptions(): Array<{ value: string; label: string }> {
  return [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last week', label: 'Last 7 Days' },
    { value: 'last month', label: 'Last 30 Days' },
    { value: 'last 3 months', label: 'Last 3 Months' },
    { value: 'last 6 months', label: 'Last 6 Months' },
    { value: 'last year', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' }
  ];
}

/**
 * Parse forward date range from string (for options expiry)
 */
export function parseForwardDateRange(rangeString: string): { startDate: string; endDate: string } | null {
  if (rangeString === 'custom') return null;
  
  const startDate = formatDate(new Date()); // Today as start
  const endDate = getForwardDate(rangeString);
  
  return { startDate, endDate };
}

/**
 * Parse date range from string (backward compatibility)
 */
export function parseDateRange(rangeString: string): { startDate: string; endDate: string } | null {
  if (rangeString === 'custom') return null;
  
  const endDate = formatDate(new Date());
  const startDate = getRelativeDate(rangeString);
  
  return { startDate, endDate };
} 